import {
  AuthConst,
  Tenant,
  UserAuthz,
  getCustomOidcEnabled,
  useLaunchDarkly,
  useLocalStorage,
  useOrgInfoStore,
  useUser,
  useUserAuthz,
} from "@p0security/frontend-core";
import {
  ForgotPasswordInputs,
  MagicLinkLoginForm,
  MagicLinkLoginInputs,
  PasswordForm,
  PasswordFormType,
  PasswordLoginInputs,
  PasswordSignUpInputs,
  UserAuthProvider,
  auth,
  idPlatform,
} from "@p0security/frontend-core/react";
import {
  Alert,
  Button,
  Card,
  Checkbox,
  CheckboxChangeEvent,
  Flex,
  Spacing,
  Typography,
} from "@p0security/ui";
import {
  browserLocalPersistence,
  browserSessionPersistence,
  setPersistence,
} from "firebase/auth";
import React, {
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useParams } from "react-router";
import { useSearchParams } from "react-router-dom";

import useUserIdentification from "../../hooks/useUserIdentification";
import { Splash } from "../Splash";
import Loader from "../common/Loader";

type LoginElements = {
  element: JSX.Element;
  heading: React.ReactNode;
  footer?: ReactElement;
};

const RememberMe: React.FC<{
  isRemember: boolean;
  setIsRemember: (value: boolean) => void;
}> = ({ isRemember, setIsRemember }) => {
  const handleRememberChange = useCallback(
    (evt: CheckboxChangeEvent) => setIsRemember(evt.target.checked),
    [setIsRemember]
  );
  return (
    <Flex vertical>
      <Checkbox checked={isRemember} onChange={handleRememberChange}>
        Stay logged in on this device?
      </Checkbox>
      <Typography.Text type="secondary">
        Avoid on public or shared devices.
      </Typography.Text>
    </Flex>
  );
};

export const Login: React.FC<React.PropsWithChildren<object>> = ({
  children,
}) => {
  return (
    <UserAuthProvider>
      <LoginContent>{children}</LoginContent>
    </UserAuthProvider>
  );
};

/** Renders three possible UIs:
 *  1. Renders a spinner while loading auth state
 *  2. If not signed in, renders a sign-in page
 *  3. If signed in, renders `children`
 */
const LoginContent: React.FC<React.PropsWithChildren<object>> = ({
  children,
}) => {
  const {
    signOut,
    signInWithEmail,
    signInWithPassword,
    signUpWithPassword,
    sendPasswordReset,
    resendEmailVerification,
    signInWithProvider,
    authError,
    needsEmailVerification,
    isInAuth,
    user,
    tenant,
  } = useUser();

  const userAuthz = useUserAuthz(signOut, user, tenant);
  const { orgSlug } = useParams();

  const [searchParams] = useSearchParams();
  const [showForm, setShowForm] = useState<PasswordFormType>("login");
  const [unexpectedSignInError, setUnexpectedSignInError] = useState<string>();

  const isNewTenant = searchParams.get("tenant") === "new";
  const [emailSent, setEmailSent] = useState<boolean>(false);
  const [passwordResetSent, setPasswordResetSent] = useState<boolean>(false);
  const [verificationEmailSent, setVerificationEmailSent] =
    useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isRemember, setIsRemember] = useLocalStorage(
    AuthConst.PERSIST_AUTH_KEY,
    false
  );
  const { orgInfo } = useOrgInfoStore();

  // register organization with LaunchDarkly
  useLaunchDarkly(
    orgSlug ?? "",
    orgInfo.status === "found" ? orgInfo.info.subscriptionPlan : undefined
  );
  // register user with logrocket and heap analytics
  useUserIdentification();

  useEffect(() => {
    if (isRemember) {
      setPersistence(auth, browserLocalPersistence);
    } else {
      setPersistence(auth, browserSessionPersistence);
    }
  }, [isRemember]);

  const handleMagicLinkLogin = useCallback(
    async ({ email }: MagicLinkLoginInputs) => {
      // At this point, inline validation has passed.
      const success = await signInWithEmail?.(email);
      if (success) {
        setEmailSent(true);
      }
    },
    [signInWithEmail]
  );

  const handlePasswordLogin = useCallback(
    async (args: PasswordLoginInputs) => {
      await signInWithPassword?.(args);
    },
    [signInWithPassword]
  );

  const handlePasswordSignUp = useCallback(
    async (args: PasswordSignUpInputs) => {
      await signUpWithPassword?.(args);
    },
    [signUpWithPassword]
  );

  const handleForgotPassword = useCallback(
    async ({ email }: ForgotPasswordInputs) => {
      setIsLoading(true);
      await sendPasswordReset?.(email);
      setIsLoading(false);
      setPasswordResetSent(true);
    },
    [sendPasswordReset]
  );

  // Auth handler - uses whichever auth provider is currently active
  const handleSignInWithProvider = useCallback(async () => {
    setUnexpectedSignInError(undefined);
    try {
      await signInWithProvider();
    } catch {
      setUnexpectedSignInError("Unable to sign in. Please try again.");
    }
  }, [signInWithProvider]);

  const handleResendVerification = useCallback(async () => {
    setIsLoading(true);
    await resendEmailVerification?.();
    setIsLoading(false);
    setVerificationEmailSent(true);
  }, [resendEmailVerification]);

  const orgName = useMemo(
    () => (tenant.state === "found" ? tenant.orgName : undefined),
    [tenant]
  );

  const useSso = useMemo(
    () => tenant.state === "found" && tenant.auth?.type === "sso",
    [tenant]
  );
  const usePassword = useMemo(
    () => tenant.state === "found" && tenant.auth?.type === "password",
    [tenant]
  );

  const signInElement = useMemo(
    (): LoginElements =>
      passwordResetSent
        ? {
            element: (
              <Typography.Paragraph>
                Check your email for a password reset link.
              </Typography.Paragraph>
            ),
            heading: "Success",
          }
        : emailSent
          ? {
              element: (
                <Typography.Paragraph>
                  Check your email for a sign in link.
                </Typography.Paragraph>
              ),
              heading: "Success",
            }
          : tenant.state === "loading"
            ? {
                element: <Typography.Paragraph>Loading</Typography.Paragraph>,
                heading: "Please hold",
              }
            : tenant.state === "found"
              ? {
                  element: (() => {
                    // When OIDC is enabled via config, show only OIDC authentication
                    if (getCustomOidcEnabled()) {
                      return (
                        <Button
                          type="primary"
                          onClick={handleSignInWithProvider}
                        >
                          Sign in with SSO
                        </Button>
                      );
                    }

                    // Otherwise, show the tenant's configured auth method
                    return useSso ? (
                      <Button onClick={handleSignInWithProvider}>
                        {idPlatform.element(tenant)}
                      </Button>
                    ) : usePassword ? (
                      <div>
                        <PasswordForm
                          showForm={showForm}
                          setShowForm={setShowForm}
                          onLogin={handlePasswordLogin}
                          onSignUp={handlePasswordSignUp}
                          onForgotPassword={handleForgotPassword}
                        />
                      </div>
                    ) : (
                      <>
                        <Typography.Paragraph>
                          We&apos;ll email you a magic link to sign in.
                        </Typography.Paragraph>
                        <MagicLinkLoginForm onLogin={handleMagicLinkLogin} />
                      </>
                    );
                  })(),
                  heading:
                    showForm === "login"
                      ? `Sign in to ${orgName ?? orgSlug ?? "your organization"}`
                      : showForm === "signup"
                        ? `Create an account for ${
                            orgName ?? orgSlug ?? "your organization"
                          }`
                        : `Forgot password?`,
                  footer: idPlatform.footer(tenant),
                }
              : {
                  element: (
                    <>
                      <Typography.Paragraph>
                        Organization not found
                      </Typography.Paragraph>
                      <Button type="primary" href="/">
                        Return home
                      </Button>
                    </>
                  ),
                  heading: "Oh no...",
                },
    [
      emailSent,
      handleForgotPassword,
      handleMagicLinkLogin,
      handlePasswordLogin,
      handlePasswordSignUp,
      handleSignInWithProvider,
      orgName,
      orgSlug,
      passwordResetSent,
      showForm,
      tenant,
      usePassword,
      useSso,
    ]
  );

  return user?.tenantId ? (
    // We're still loading roles for user
    userAuthz.size === 0 ? (
      <Loader />
    ) : needsEmailVerification && !user.emailVerified ? (
      <Splash>
        <>
          <Card title="Email verification required">
            <div className="flex flex-col">
              {isNewTenant && tenant.state === "found" && (
                <Spacing mb={20}>
                  <Alert
                    type="success"
                    showIcon
                    message="Account created successfully"
                    description={`Your account for ${
                      orgName ?? orgSlug
                    } was successfully created. Please log in with your email to get started here.`}
                  />
                </Spacing>
              )}
              <Typography.Paragraph>
                Please verify your email address to sign in.
              </Typography.Paragraph>
              {verificationEmailSent ? (
                <Spacing mb={20}>
                  <Alert
                    type="success"
                    showIcon
                    message="Verification email sent"
                    description={`Check your inbox for a verification email. If you don't see it, check your spam folder.`}
                  />
                </Spacing>
              ) : (
                <Button type="primary" onClick={handleResendVerification}>
                  Resend verification email
                </Button>
              )}
            </div>
          </Card>
        </>
      </Splash>
    ) : (
      <Tenant.Provider value={user.tenantId}>
        <UserAuthz.Provider value={userAuthz}>{children}</UserAuthz.Provider>
      </Tenant.Provider>
    )
  ) : isInAuth || isLoading ? (
    <Loader />
  ) : signInElement ? (
    <Splash>
      <>
        <Card title={signInElement.heading}>
          <Flex gap="middle" vertical>
            {isNewTenant && tenant.state === "found" && (
              <Spacing mb={20}>
                <Alert
                  type="success"
                  showIcon
                  message="Account created successfully"
                  description={`Your account for ${
                    orgName ?? orgSlug
                  } was successfully created. Please log in with your email to get started here.`}
                />
              </Spacing>
            )}
            {signInElement.element}
            {(authError ?? unexpectedSignInError) && (
              <Spacing mb={20}>
                <Alert
                  type="error"
                  showIcon
                  message={authError ?? unexpectedSignInError}
                />
              </Spacing>
            )}
            <RememberMe {...{ isRemember, setIsRemember }} />
            {signInElement.footer}
          </Flex>
        </Card>
      </>
    </Splash>
  ) : (
    <Splash>Page not found</Splash>
  );
};
